<script lang="ts">
	import { CircleWithCheck, PieTimetable } from "@hitit/new_ui/src";
	import { api, t } from "@hitit/hub/src/stores";
	import moment from "moment";
	import { formatISO } from "date-fns";
	import { onDestroy } from "svelte";
	import ComponentCover from "../common/ComponentCover.svelte";

	export let storeIds: string[] = [];
	export let range = {
		start: new Date(),
		end: new Date(),
	};

	type ChartData = {
		time: string;
		count: number;
	};
	const dummyData = ["18", "13", "8"];
	const dummyPieColors = ["#f0f0f4", "#f0f0f4", "#f0f0f4"];
	const dummyBetweenColors = ["#bcbfc3", "#bcbfc3", "#bcbfc3"];
	const originPieColors = ["#6591fa", "#bcbfc3", "#e6e7ed"];
	const originBetweenColors = ["#6591fa", "#6d6f76", "#6d6f76"];
	const margin = {
		left: 50,
		right: 50,
		top: 50,
		bottom: 50,
	};
	let addHour = 1;
	let noDatas = false;
	let pieColors = originPieColors;
	let betweenColors = originBetweenColors;
	let chartData: ChartData[] = [];
	function getHourlyCount(range: { start: Date; end: Date }, storeId: string) {
		const { start, end } = range;
		return $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(start),
			endTimeInTimeZone: formatISO(end),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "HOURLY",
			storeid: storeId,
		});
	}
	// Caution : amPm string will be splited by "|".
	// Like "am|pm" , "오전|오후"
	function parsedTopTimeRange(amPm: string, hour?: number | string) {
		if (hour === undefined) return "";
		hour = Number(hour);
		const [am, pm] = amPm.split("|");
		const start = moment(hour, "H").format("hh");
		const end = moment(hour + 1, "H").format("hh");
		const addPrefix = (time: number) => (time % 24 >= 12 ? pm : am);
		const startTime = `${addPrefix(hour)} ${start}:00`;
		const endTime = `${addPrefix(hour + 1)} ${end}:00`;

		return startTime + " - " + endTime;
	}

	//Do
	$: (async (__storeIds: string[], __range: { start: Date; end: Date }) => {
		try {
			const map = new Map();
			const allRanking = __storeIds.map((id) => getHourlyCount(__range, id));
			const promises = await Promise.all(allRanking);
			for (const result of promises) {
				const salesReport = result.data.salesReports;

				for (const report of salesReport) {
					const { count, hour } = report;
					const key = `${hour}`;
					const originValue = map.get(hour) || 0;
					const newValue = originValue + count;
					map.set(key, newValue);
				}
			}

			const topThreePicktime = [...map]
				.sort(([, aCount], [, bCount]) => bCount - aCount)
				.slice(0, 3)
				.map(([time, count]) => {
					return {
						time,
						count,
					};
				});

			if (topThreePicktime.length === 0) {
				pieColors = dummyPieColors;
				betweenColors = dummyBetweenColors;
				addHour = 2;
				noDatas = true;
				chartData = dummyData.map((v) => ({ time: v, count: 0 }));
			} else {
				pieColors = originPieColors;
				betweenColors = originBetweenColors;
				addHour = 1;
				noDatas = false;
				chartData = topThreePicktime;
			}
		} catch (error) {
			console.log(error);
		}
	})(storeIds, range);

	$: if (graph && chartData) {
		if (chart) {
			chart.remove();
			window.removeEventListener("resize", __resizeEvent__);
		}
		const datas = chartData.map((v, i) => {
			const start = moment(Number(v.time), "H").toDate();
			const end = moment(Number(v.time) + addHour, "H").toDate();
			return {
				start,
				end,
				sorting: i,
				color: pieColors[i],
				betweenTextcolor: betweenColors[i],
			};
		});
		const { node, resizeEvent } = PieTimetable(graph, datas, { margin });
		chart = node;
		__resizeEvent__ = resizeEvent;
	}

	onDestroy(() => {
		window.removeEventListener("resize", __resizeEvent__);
	});

	//
	let graph: HTMLDivElement;
	let chart: SVGElement;
	let __resizeEvent__ = () => {};

</script>

<div class="container">
	<div class="titles">
		<span class="sub-title"> {$t('hub.dashboard.graph.peak-time')} </span>
		<h2 class="main-title">
			<span class="icon">
				<CircleWithCheck />
			</span>
			PEAK TIME!
		</h2>
	</div>
	<div class="graph" bind:this={graph} />
	<div class="capsules" class:noDatas>
		<div class="capsule top-1">
			<span class="title">TOP 1</span>
			<p class="time">
				{parsedTopTimeRange($t('hub.dashboard.graph.Am|Pm'), chartData[0]?.time)}
			</p>
		</div>
		<div class="capsule top-2">
			<span class="title">TOP 2</span>
			<p class="time">
				{parsedTopTimeRange($t('hub.dashboard.graph.Am|Pm'), chartData[1]?.time)}
			</p>
		</div>
		<div class="capsule top-3">
			<span class="title">TOP 3</span>
			<p class="time">
				{parsedTopTimeRange($t('hub.dashboard.graph.Am|Pm'), chartData[2]?.time)}
			</p>
		</div>
	</div>
	{#if noDatas}
		<ComponentCover alignCenter coverColor={'transparents'}>
			<span class="description"> {$t('hub.dashboard.no_enough_data')} </span>
		</ComponentCover>
	{/if}
</div>

<style lang="scss">
	@import "../../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		box-sizing: border-box;
		padding: 20px 10px;
		position: relative;
		.description {
			font-size: 1.25rem;
			font-weight: 500;
			color: #6d6f76;
		}

		.graph {
			width: 100%;
			height: 312px;
			:global(svg g text.hour-label) {
				font-weight: 500;
				font-size: 0.875rem;
				fill: #bcbfc3;
			}
		}
		.titles {
			display: flex;
			flex-direction: column;
			gap: 10px;

			.sub-title {
				font-size: 1.25rem;
				font-weight: 500;
				color: #6d6f76;
			}
			.main-title {
				display: flex;
				align-items: center;
				gap: 10px;
				font-weight: 600;
				font-size: 1.875rem;
				color: #46474b;
				/* Poppins */

				.icon {
					width: 26px;
					height: 26px;
				}
			}
		}

		.capsules {
			display: flex;
			flex-direction: column;
			width: 100%;
			gap: 10px;
			&.noDatas {
				.capsule {
					background-color: #f0f0f4;
					.title,
					.time {
						display: none;
					}
				}
			}

			.capsule {
				display: flex;
				flex-direction: column;
				gap: 4px;
				justify-content: space-around;
				padding: 10px 30px;
				border-radius: 38px;
				box-sizing: border-box;
				color: #46474b;
				font-size: 1rem;
				.title {
					/* Poppins */
					font-weight: 550;
				}
				.time {
					font-weight: 500;
				}
			}
			.top-1 {
				width: 100%;
				background-color: #6591fa;
				color: white;
				min-height: 76px;
				font-size: 1.25rem;
				.title {
					font-weight: 600;
				}
			}
			.top-2 {
				width: calc(100% - 30px);
				color: #46474b;
				background-color: #bcbfc3;
				min-height: 65px;
			}
			.top-3 {
				color: #6d6f76;
				width: calc(100% - 60px);
				background-color: #f0f0f4;
				min-height: 66px;
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 30px;
			.graph {
				height: 602px;
				:global(svg g text.hour-label) {
					font-size: 1rem;
				}
			}
			.titles {
				gap: 20px;
				.sub-title {
					font-size: 1.5rem;
				}
				.main-title {
					font-size: 2.1875rem;
					.icon {
						width: 30px;
						height: 30px;
					}
				}
			}

			.capsules {
				gap: 20px;
				.capsule {
					justify-content: start;
					flex-direction: row;
					align-items: center;
					gap: 20px;
					padding: 0;
					padding-left: 40px;
					font-size: 1.25rem;
				}
				.top-1 {
					width: calc(100% * (691 / 727));
					min-height: 65px;
					font-size: 1.5rem;
				}
				.top-2 {
					width: calc(100% * (616 / 727));
					min-height: 60px;
				}
				.top-3 {
					width: calc(100% * (551 / 727));
					min-height: 60px;
				}
			}
		}
	}

</style>
